import React, { useState } from "react";
import styled from "styled-components";
import { actOnEnterOrSpace, slugify } from "../../utils";
import { IconArrowDown, colorFunctionalGray70, colorLayoutBorder, spacingM, spacingMl, spacingS, } from "@rhumbix/rmbx_design_system_design_tokens";
const getMenuId = (readableLabel) => `sidebar-menu-${slugify(readableLabel)}`;
// we setup list views/titles/paths for every level of the path, folders included, but we don't want to
// include these keys in the keys that get converted into folders.
const keysToSkip = ["listView", "navTitle", "path"];
/**
 * A recursive function to build out nested folders. If a list view has a path key and use_nav_folder_paths is false,
 * it is a leaf note, and should be a clickable route. If not, then it is a folder, and
 * we should wrap the nested folders in a collapsible folder and continue to resurse down.
 * @param nestedFolders - mapping of the nested subfolders
 * @param expandedMenuState - key/value pairs to know if menu is expanded or not
 * @param renderMenuItemLink - method from main repo to render menu items
 * @returns folders and leaf nodes for the menu
 */
const getNestedFolders = (nestedFolders, expandedMenuState, renderMenuItemLink, isMini = false) => {
    return Object.keys(nestedFolders)
        .filter(key => !keysToSkip.includes(key)) // make sure we don't add menu items for known list view keys
        .sort()
        .map((subfolder) => {
        if (nestedFolders[subfolder]?.path && !nestedFolders[subfolder]?.listView?.use_nav_folder_paths) {
            // this is a leaf node - so will return a single JSX.Element hitting the top condition of this method
            return (React.createElement(LeafMenuItem, { "$isMini": isMini, key: getMenuId(nestedFolders[subfolder].path) }, renderMenuItemLink(nestedFolders[subfolder])));
        }
        return (React.createElement(CollapsibleFolderMenu, { route: nestedFolders[subfolder], isExpanded: !!expandedMenuState?.[getMenuId(subfolder)], isRootMenu: false, isMini: isMini, key: slugify(subfolder), menuTitle: subfolder, renderMenuItemLink: renderMenuItemLink }, getNestedFolders(nestedFolders[subfolder], expandedMenuState, renderMenuItemLink, isMini)));
    });
};
/**
 * A nested folder menu component that can take in a mapping of folders and convert it into a
 * collapsing folder menu. nestedListViews should have all the necessary information to create menu items.
 */
const CollapsibleFolderMenu = ({ children = [], isExpanded: origIsExpanded, isMini, isRootMenu, menuTitle, nestedListViews, renderMenuItemLink, route, }) => {
    const menuId = getMenuId(menuTitle);
    const [expandedMenuState, setExpandedMenuState] = useState({
        [menuId]: origIsExpanded,
    });
    const handleExpandClick = (e) => {
        const isExpanded = !!expandedMenuState?.[e.currentTarget.id];
        setExpandedMenuState({ ...expandedMenuState, [menuId]: !isExpanded });
        e.stopPropagation();
    };
    const MenuComponent = isMini ? MiniMenuGroup : MenuGroup;
    return (React.createElement(MenuComponent, { key: menuId, menuTitle: menuTitle, menuId: menuId, handleExpandClick: handleExpandClick, renderMenuItemLink: renderMenuItemLink, route: route, isExpanded: !!expandedMenuState[menuId], isRootMenu: isRootMenu }, isRootMenu && nestedListViews?.[menuTitle]
        ? getNestedFolders(nestedListViews[menuTitle], expandedMenuState, renderMenuItemLink, isMini)
        : children));
};
export default CollapsibleFolderMenu;
/**
 * This will display the rotating arrow, folder, and, if expanded, the menu group's children
 * @param props
 * @returns
 */
const MenuGroup = (props) => {
    const { route, children, handleExpandClick, isExpanded, menuId, menuTitle, renderMenuItemLink } = props;
    return (React.createElement("div", null,
        React.createElement(CollapsibleMenuTitle, { onKeyDown: e => actOnEnterOrSpace(e, () => handleExpandClick), tabIndex: 0 },
            React.createElement("div", { onClick: handleExpandClick, role: "button", id: menuId, style: { display: "flex", alignSelf: "center" } },
                React.createElement(RotatingArrowIcon, { "$isExpanded": isExpanded })),
            React.createElement(MenuTitleContainer, { role: "button" }, route?.path ? (React.createElement(IconSpacer, null, renderMenuItemLink(route))) : (React.createElement(IconSpacer, { onClick: handleExpandClick }, menuTitle)))),
        isExpanded && React.createElement(MenuItemUl, null, children)));
};
const MiniMenuGroup = (props) => {
    const { route, children, handleExpandClick, isExpanded, isRootMenu, menuId, menuTitle, renderMenuItemLink } = props;
    return (React.createElement("div", { className: "mini-sidebar-custom-list-item" },
        React.createElement(MiniCollapsibleMenuTitle, { onKeyDown: e => actOnEnterOrSpace(e, () => handleExpandClick), tabIndex: 0 },
            React.createElement(MiniMenuTitleContainer, { role: "button" }, route?.path ? (React.createElement(IconSpacer, null, renderMenuItemLink(route))) : (React.createElement(IconSpacer, { onClick: handleExpandClick }, menuTitle))),
            React.createElement(MiniArrowContainer, { onClick: handleExpandClick, onKeyDown: e => actOnEnterOrSpace(e, () => handleExpandClick), role: "button", id: menuId, tabIndex: -1 },
                React.createElement(MiniRotatingArrowIcon, { "$isExpanded": isExpanded }))),
        isExpanded && React.createElement(MiniMenuItemUl, null, children)));
};
// STYLED COMPONENTS
const CollapsibleMenuTitle = styled.div `
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 14px;
    cursor: pointer !important;
`;
const IconSpacer = styled.div `
    margin-left: ${spacingS};
    width: 100%;
`;
const MenuTitleContainer = styled.div `
    display: flex;
    align-items: center;
    width: 80%;
    user-select: none;
`;
const RotatingArrowIcon = styled(IconArrowDown).withConfig({
    shouldForwardProp: prop => ["children", "variant"].includes(prop),
}) `
    width: 14px;
    height: 14px;
    transform: rotate(${props => (props?.$isExpanded ? 0 : -90)}deg);
    transition: transform 0.3s ease;
    margin-right: ${spacingS};
    margin-top: ${spacingM};
`;
const MenuItemUl = styled.ul `
    padding-left: ${spacingMl};
`;
const LeafMenuItem = styled.li.withConfig({
    shouldForwardProp: prop => ["children", "variant"].includes(prop),
}) `
    margin-left: ${props => (props?.$isMini ? 0 : spacingMl)} !important;
    margin-top: ${props => (props?.$isMini ? "-1px" : spacingM)} !important;
    border-top: ${props => (props?.$isMini ? `1px solid ${colorLayoutBorder}` : 0)} !important;
`;
// Minimized Menu Styling
const MiniCollapsibleMenuTitle = styled.div `
    border-top: 1px solid ${colorLayoutBorder};
    margin-top: -1px;
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 14px;
    cursor: pointer !important;
    justify-content: space-between;
`;
const MiniMenuTitleContainer = styled.div `
    display: flex;
    align-items: center;
    width: 90%;
    user-select: none;
    cursor: pointer;
    position: relative;
    white-space: normal;
    overflow-wrap: break-word;
`;
const MiniArrowContainer = styled.div `
    height: 50px;
    width: 36px;
    display: flex;
    align-items: center;
    align-self: center;
`;
const MiniRotatingArrowIcon = styled(IconArrowDown).withConfig({
    shouldForwardProp: prop => ["children", "variant"].includes(prop),
}) `
    width: 14px;
    height: 14px;
    transform: rotate(${props => (props?.$isExpanded ? 0 : -90)}deg);
    transition: transform 0.3s ease;
    color: ${colorFunctionalGray70} !important;
`;
const MiniMenuItemUl = styled.ul `
    width: 100%;
    background: #fff;
    border-top: 1px solid ${colorLayoutBorder};
`;
